import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { Icon } from "@/components/Icon";
import { InflowLogo } from "@/components/InflowLogo";
import { tg } from "@/lib/telegram";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";

/**
 * InstructionKit — переиспользуемый набор для всех инструкций-подсказок:
 *  - дрібні інлайн-іконки (статичні SVG);
 *  - мок-картки розділів (1:1 з реальними екранами);
 *  - InstructionFlow — рушій (прогрес, слайди, навігація).
 *
 * Анімовані емодзі лише з робочого набору. НЕ використовуємо 💬 (chat) і
 * 🚀 (rocket) — їх Lottie забагований (порожні кадри). Де треба «галочку»/
 * «чат» статично — беремо SVG нижче, а не TgEmoji.
 */

// ---- статичні іконки ---------------------------------------------------
const S = "stroke-current";
export function ICheck({ s = 12 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.5 10 17 19 6.5" />
    </svg>
  );
}
export function ISearch({ s = 14 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" />
    </svg>
  );
}
export function IRefresh({ s = 16 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 4v5h-5" />
    </svg>
  );
}
export function IChat({ s = 16 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.5 8.5 0 0 1-12.3 7.6L3 21l1.9-5.7A8.5 8.5 0 1 1 21 11.5Z" />
    </svg>
  );
}
export function IChevron({ s = 14 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}
function ITelegram({ s = 16 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
      <path d="M21.9 4.3 18.7 20c-.2 1-.9 1.25-1.78.78l-4.9-3.6-2.36 2.27c-.26.26-.48.48-.98.48l.35-5 9.1-8.22c.4-.35-.08-.55-.62-.2L6.26 13.9l-4.85-1.52c-1.05-.33-1.07-1.05.22-1.55L20.6 3.2c.88-.33 1.65.2 1.3 1.1z"/>
    </svg>
  );
}
function IArrowUpRight({ s = 14 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 17 17 7M8 7h9v9" />
    </svg>
  );
}
function IDoc({ s = 16 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3v5h5" /><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    </svg>
  );
}
function IMic({ s = 16 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </svg>
  );
}
function IPlay({ s = 16 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
  );
}
function IPlus({ s = 18 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
  );
}
function IFlame({ s = 18 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.4-.5-2-1-3-1.07-2.14-.22-4.05 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.15.43-2.3 1-3a2.5 2.5 0 0 0 2.5 2.5Z" /></svg>
  );
}
function IGlobe({ s = 18 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18" />
    </svg>
  );
}
function ILaptop({ s = 18 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="5" width="16" height="10" rx="1" /><path d="M2 19h20" />
    </svg>
  );
}
function IGift({ s = 18 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7S11 3 8.5 3 5.5 5.5 7 7M12 7s1-4 3.5-4S18.5 5.5 17 7" />
    </svg>
  );
}
function IBolt({ s = 18 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M13 2 4 14h6l-1 8 9-12h-6z" /></svg>
  );
}
function IUsers({ s = 18 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3.2" /><path d="M3 20a6 6 0 0 1 12 0" /><path d="M16 5.5a3 3 0 0 1 0 5.4M21.5 20a6 6 0 0 0-4.5-5.8" />
    </svg>
  );
}
function IArrowDown({ s = 18 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" className={S} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M6 13l6 6 6-6" />
    </svg>
  );
}
// Фірмовий значок inFLOW (той самий глиф, що в логотипі) — для аватарів бота.
// viewBox зміщено, щоб глиф був РІВНО по центру (шлях трохи верхній у 0 0 7 7).
function InflowGlyph({ s = 14, color = "#fff" }: { s?: number; color?: string }) {
  return (
    <svg width={s} height={s} viewBox="-0.03 -0.43 7 7" fill="none">
      <path d="M3.86571 0.00100987C4.70872 0.00100987 5.47668 0.344409 6.03334 0.900108C6.59101 1.4548 6.93546 2.22376 6.93546 3.07081C6.93546 3.91382 6.59202 4.68177 6.03636 5.23848C5.47668 5.79716 4.70872 6.14056 3.86571 6.14056C3.01866 6.14056 2.24975 5.79615 1.69505 5.24146C1.1394 4.68178 0.795955 3.91382 0.795955 3.07081L1.58993 2.27481L2.38589 3.07081C2.38589 3.48028 2.55113 3.85274 2.81745 4.11906C3.08474 4.38443 3.45623 4.55063 3.86571 4.55063C4.27519 4.55063 4.64764 4.38543 4.91397 4.11906C5.18029 3.85274 5.34553 3.48028 5.34553 3.07081C5.34553 2.66128 5.17933 2.28984 4.913 2.02351C4.64764 1.75618 4.27519 1.59099 3.86571 1.59099C2.58714 1.59099 1.27856 1.59195 0 1.59195L0.795955 0.795998L0 0C1.27856 0 2.58714 0.00100987 3.86571 0.00100987Z" fill={color} />
    </svg>
  );
}

// ---- спільні дрібниці --------------------------------------------------
function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={"w-full rounded-2xl bg-surface p-3 shadow-card " + className}>{children}</div>;
}
const lines = (ws: string[]) => (
  <div className="space-y-1.5">
    {ws.map((w, i) => <div key={i} className={"h-2 rounded-full bg-ink/10 " + w} />)}
  </div>
);
function FeedCardFace({ summary = true }: { summary?: boolean }) {
  return (
    <>
      <div className="flex items-center gap-1.5 pr-2">
        <span className="truncate rounded-full bg-primary/10 px-2 py-0.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-primary">таргет</span>
        <span className="shrink-0 rounded-full bg-ink/5 px-2 py-0.5 font-mono text-[0.625rem] text-ink-dim">TG-чат</span>
        <span className="ml-auto shrink-0 font-mono text-[0.625rem] text-ink-dim">щойно</span>
      </div>
      {summary && <div className="mt-2">{lines(["w-11/12", "w-8/12"])}</div>}
    </>
  );
}

// ---- мок-картки --------------------------------------------------------

// Стрічка = стопка карток (одна на одну)
export function MockFeedStack() {
  return (
    <div className="relative mx-auto w-full max-w-[16.25rem] pt-2">
      <div className="absolute inset-x-3 -top-0 h-10 rounded-2xl bg-surface/70 shadow-card-soft" />
      <div className="absolute inset-x-1.5 top-1 h-12 rounded-2xl bg-surface/85 shadow-card-soft" />
      <div className="relative">
        <span className="absolute -right-1.5 -top-1.5 z-10 rounded-full bg-primary px-2 py-0.5 font-mono text-[0.5625rem] font-bold uppercase tracking-[0.12em] text-white shadow-md">new</span>
        <Card><FeedCardFace /></Card>
      </div>
    </div>
  );
}

// Розкрита картка вакансії (контакти + повний текст)
export function MockVacancyOpen() {
  return (
    <Card>
      <FeedCardFace summary={false} />
      <div className="mt-2">{lines(["w-full", "w-11/12", "w-9/12"])}</div>
      {/* Контакти замовника — 1:1 з реальним (оранжева CTA-кнопка з TG) */}
      <div className="mt-2.5">
        <div className="mb-1.5 font-mono text-[0.5625rem] uppercase tracking-[0.1em] text-ink-dim">Контакти замовника</div>
        <div className="flex items-center gap-2.5 rounded-2xl bg-primary px-3 py-2.5 text-white shadow-card">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/20"><ITelegram s={15} /></span>
          <span className="flex-1 truncate font-display text-[0.8125rem] font-bold">@client_marketing</span>
          <IArrowUpRight s={14} />
        </div>
      </div>
    </Card>
  );
}

// Екран генерації відгуку — 1:1 з реальним AIResponseSheet (вибір стилю)
export function MockGenReply() {
  const styles: [string, string][] = [
    ["Біль і рішення", "наш промпт"],
    ["Конкретика з досвіду", "наш промпт"],
    ["Мій стиль", "твій промпт"],
  ];
  return (
    <Card>
      <div className="mb-2 flex items-center gap-2">
        <TgEmoji char="🤖" size={18} />
        <span className="font-display text-[0.8125rem] font-bold text-ink">Відповідь на вакансію</span>
      </div>
      <div className="space-y-1.5">
        {styles.map(([l, s]) => (
          <div key={l} className="flex items-center gap-2 rounded-xl border border-ink/[0.06] bg-bg/60 px-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="font-display text-[0.75rem] font-semibold text-ink">{l}</div>
              <div className="text-[0.625rem] text-ink-dim">{s}</div>
            </div>
            <span className="text-primary"><IChevron s={13} /></span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// Відгуки + рейтинг (без анімованих емодзі — статичні SVG)
export function MockVacancyFiles() {
  const rows: [string, string][] = [
    ["Бриф на проєкт.pdf", "PDF · 89 КБ"],
    ["Кошторис.xlsx", "Таблиця · 2 КБ"],
  ];
  return (
    <Card>
      <div className="mb-2 flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
        <span className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-ink-dim">
          Додаткові матеріали
        </span>
      </div>
      <div className="mb-2 grid grid-cols-3 gap-1.5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-10 rounded-[0.5rem] bg-ink/[0.06]" />
        ))}
      </div>
      <div className="space-y-1.5">
        {rows.map(([n, sub]) => (
          <div
            key={n}
            className="flex items-center gap-2 rounded-xl bg-ink/[0.04] px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-[0.71875rem] text-ink">{n}</div>
              <div className="text-[0.625rem] text-ink-dim">{sub}</div>
            </div>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 font-display text-[0.625rem] font-bold text-primary">
              Відкрити
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function MockReviews() {
  return (
    <Card>
      <div className="flex items-center gap-2.5">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] bg-primary/10 text-primary"><IChat s={16} /></span>
        <div className="min-w-0 flex-1">
          <span className="mb-0.5 inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[0.625rem] font-semibold text-emerald-600">
            <span className="text-emerald-600"><ICheck s={11} /></span> Надійний замовник
          </span>
          <div className="truncate font-display text-[0.8125rem] font-semibold text-ink">Відгуки про @client</div>
          <div className="text-[0.6875rem] text-ink-dim">★ 4.8 · 12 голосів · 5 відгуків</div>
        </div>
      </div>
    </Card>
  );
}

// Оновлення стрічки — кнопки СТАТИЧНІ (без обертання)
export function MockRefresh() {
  return (
    <div className="w-full">
      <div className="flex items-center justify-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-surface-soft text-primary shadow-card-soft"><IRefresh /></span>
        <span className="grid h-10 w-10 place-items-center rounded-full bg-surface-soft text-primary shadow-card-soft"><ISearch s={16} /></span>
      </div>
    </div>
  );
}

// Профіль: картка «Управління підпискою» (1:1 з SubscriptionRow)
export function MockSubRow() {
  return (
    <div className="w-full rounded-2xl border border-emerald-200/50 bg-gradient-to-br from-emerald-50 to-emerald-100/50 p-3.5 shadow-card-soft">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] bg-emerald-500 text-white"><ICheck s={16} /></span>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[0.875rem] font-bold text-ink">Управління підпискою</div>
          <div className="mt-0.5 text-[0.71875rem] text-ink-dim">PRO активна · 24 дні · до 12 липня</div>
        </div>
        <span className="text-ink/40"><IChevron s={14} /></span>
      </div>
    </div>
  );
}

// Профіль: рядок-розділ (1:1 з ProfileAccordion)
function ProfileRow({ icon, title, desc }: { icon: ReactNode; title: string; desc: string }) {
  return (
    <div className="w-full rounded-2xl bg-surface p-3.5 shadow-card-soft">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] bg-primary/10 text-primary">{icon}</span>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[0.875rem] font-bold text-ink">{title}</div>
          <div className="mt-0.5 text-[0.71875rem] leading-snug text-ink-dim">{desc}</div>
        </div>
        <span className="text-ink/40"><IChevron s={14} /></span>
      </div>
    </div>
  );
}
export function MockAboutYou() {
  return <ProfileRow icon={<IDoc s={16} />} title="Про тебе" desc="Опис для AI — для генерації відповідей на вакансії" />;
}
export function MockAddGroups() {
  return <ProfileRow icon={<IPlus s={16} />} title="Додати свої групи" desc="Telegram-канали або чати, які бот моніторитиме персонально" />;
}
export function MockSpheresRow() {
  return <ProfileRow icon={<IFlame s={16} />} title="Сфери вакансій" desc="Категорії послуг, які тебе цікавлять" />;
}
export function MockSourcesRow() {
  return <ProfileRow icon={<IGlobe s={16} />} title="Джерела вакансій" desc="Звідки збирати вакансії — Telegram, Threads, Freelancehunt, Finder" />;
}
export function MockFormatRow() {
  return <ProfileRow icon={<IDoc s={16} />} title="Оформлення вакансій" desc="Повний текст чи короткий AI-переказ" />;
}
export function MockWorkFormatRow() {
  return <ProfileRow icon={<ILaptop s={16} />} title="Формат вакансій" desc="Онлайн, офлайн чи гібрид — обираєш, які отримувати" />;
}
export function MockBirthdayRow() {
  return <ProfileRow icon={<IGift s={16} />} title="День народження" desc="Вкажи дату — і отримай подарунок у свій день" />;
}

// Вікно telegram-чату (люди постять вакансії) → inFLOW збирає у стрічку
export function MockChatsCollect() {
  return (
    <div className="w-full">
      {/* Вікно чату */}
      <div className="overflow-hidden rounded-2xl bg-surface shadow-card">
        <div className="flex items-center gap-2 border-b border-ink/[0.06] px-3 py-2">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-violet-400 text-[0.6875rem] font-bold text-white">М</span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[0.71875rem] font-bold text-ink">Маркетинг · чат</div>
            <div className="text-[0.5625rem] font-medium text-emerald-500">1 240 онлайн</div>
          </div>
        </div>
        <div className="px-3 py-2.5">
          <div className="w-fit max-w-[92%] rounded-2xl rounded-tl-sm bg-bg px-3 py-2 text-[0.71875rem] leading-snug text-ink">
            Шукаю таргетолога для запуску реклами в Instagram. Бюджет обговорюємо
          </div>
        </div>
      </div>
      {/* Стрілка вниз */}
      <div className="my-2 flex justify-center text-primary/50">
        <IArrowDown s={18} />
      </div>
      {/* твоя стрічка — картка 1:1 з реальною */}
      <div className="mb-1.5 flex items-center gap-1.5 px-0.5">
        <InflowLogo size={13} />
        <span className="font-mono text-[0.53125rem] font-bold uppercase tracking-[0.08em] text-ink-dim">твоя стрічка</span>
      </div>
      <div className="relative">
        <span className="absolute -right-1.5 -top-1.5 z-10 rounded-full bg-primary px-2 py-0.5 font-mono text-[0.5rem] font-bold uppercase tracking-[0.1em] text-white shadow-md">new</span>
        <div className="rounded-2xl bg-surface p-3 shadow-card">
          <div className="flex items-center gap-1.5 pr-2">
            <span className="truncate rounded-full bg-primary/10 px-2 py-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-primary">таргетована реклама</span>
            <span className="shrink-0 rounded-full bg-ink/5 px-2 py-0.5 font-mono text-[0.5625rem] text-ink-dim">TG-чат</span>
            <span className="ml-auto shrink-0 font-mono text-[0.5625rem] text-ink-dim">щойно</span>
          </div>
          <div className="mt-2 text-[0.75rem] leading-relaxed text-ink">Шукаю таргетолога для запуску реклами в Instagram. Бюджет обговорюємо.</div>
          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="text-[0.9375rem] leading-none text-primary">★★★★★</span>
              <span className="font-mono text-[0.5625rem] text-ink-dim">4.8 · 24 голоси</span>
            </div>
            <span className="text-ink-dim"><IChevron s={13} /></span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Картки квестів — 1:1 з реальними (icon-tile + нагорода + caption + прогрес)
export function MockQuest() {
  const quests = [
    { icon: <IBolt s={16} />, title: "Згенеруй 10 відповідей", reward: "+20 ₴", frac: "6 / 10", pct: 60 },
    { icon: <IUsers s={16} />, title: "Запроси 10 рефералів", reward: "+25 ₴", frac: "3 / 10", pct: 30 },
  ];
  return (
    <div className="w-full space-y-2">
      {quests.map((q) => (
        <div key={q.title} className="rounded-2xl bg-surface p-3.5 shadow-card">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] bg-primary/10 text-primary">{q.icon}</span>
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="font-display text-[0.84375rem] font-semibold leading-snug text-ink">{q.title}</div>
                <div className="shrink-0 font-mono text-[0.75rem] font-bold text-ink">{q.reward}</div>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink/5">
                <div className="h-full bg-primary" style={{ width: `${q.pct}%` }} />
              </div>
              <div className="mt-1.5 flex items-center justify-between font-mono text-[0.6875rem] text-ink-dim">
                <span>{q.frac}</span>
                <span>{q.pct}%</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Реферали — блок «Як це працює» 1:1 з реальною сторінкою (RewardRow)
export function MockReferral() {
  const rewards = [
    { amount: "+2 ₴", label: "За реєстрацію друга", hint: "Коли він підпишеться на наш канал" },
    { amount: "+297 ₴", label: "За першу підписку друга", hint: "30% від тарифу 990 ₴/міс." },
    { amount: "+147 ₴", label: "За кожне продовження", hint: "15% довічно, поки платить" },
  ];
  return (
    <div className="w-full">
      <h3 className="mb-2.5 font-display text-[0.75rem] font-semibold uppercase tracking-[0.06em] text-ink-dim">
        Як це працює
      </h3>
      <div className="overflow-hidden rounded-ios-card bg-surface px-1.5 py-1 shadow-card-soft">
        {rewards.map((r, i) => (
          <div
            key={r.amount}
            className={"flex items-center gap-3 px-4 py-3 " + (i < rewards.length - 1 ? "border-b border-line" : "")}
          >
            <div className="w-[4.875rem] shrink-0 font-display text-[1.125rem] font-bold tabular-nums text-primary">{r.amount}</div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-[0.8125rem] font-semibold text-ink">{r.label}</div>
              <div className="mt-0.5 text-[0.6875rem] text-ink-dim">{r.hint}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Обмін реферальних грошей на дні підписки (owner 2026-07-29): показуємо
// саму суть — баланс перетворюється на дні, і це дешевше за купівлю.
export function MockReferralExchange() {
  return (
    <div className="w-full">
      <div className="rounded-ios-card bg-surface p-4 shadow-card-soft">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 text-center">
            <div className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim mb-1">
              Твій баланс
            </div>
            <div className="font-display text-[1.25rem] font-bold leading-none text-ink">
              750 ₴
            </div>
          </div>
          <span className="shrink-0 text-primary">
            <Icon name="arrow-right" size={20} />
          </span>
          <div className="flex-1 text-center">
            <div className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim mb-1">
              Отримаєш
            </div>
            <div className="font-display text-[1.25rem] font-bold leading-none text-primary">
              півроку
            </div>
          </div>
        </div>
        <div className="mt-3 border-t border-line pt-2.5 text-center text-[0.71875rem] leading-snug text-ink-dim [text-wrap:balance]">
          25 ₴ за день замість ~33 ₴ при звичайній оплаті.
        </div>
      </div>
    </div>
  );
}

// Повідомлення бота з вакансією (як у Telegram) + inline-кнопка під ним
export function MockBotMessage() {
  return (
    <div className="w-full">
      <div className="flex items-start gap-2">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary"><InflowGlyph s={13} /></span>
        <div className="min-w-0 flex-1">
          <div className="rounded-2xl rounded-tl-sm bg-surface px-3 py-2 shadow-card-soft">
            <div className="text-[0.71875rem] leading-snug text-ink">
              Шукаю таргетолога для запуску реклами в Instagram. Бюджет обговорюємо, деталі в особисті.
            </div>
            <div className="mt-2 space-y-1 text-[0.65625rem] text-ink-dim">
              <div><span className="font-semibold text-ink/70">Сфера:</span> Таргетована реклама</div>
              <div><span className="font-semibold text-ink/70">Джерело:</span> Telegram-чат</div>
              <div><span className="font-semibold text-ink/70">Контакт:</span> @client_mkt</div>
            </div>
          </div>
          {/* inline-кнопка як у боті (менше заокруглення — ближче до квадратного) */}
          <div className="mt-1 rounded-lg bg-surface px-3 py-2 text-center text-[0.6875rem] font-bold text-primary shadow-card-soft">
            Згенерувати відповідь
          </div>
        </div>
      </div>
    </div>
  );
}

// База знань — 1:1 з реальною (картка розділів + темна «Стати спікером»)
export function MockKB() {
  const rows: { icon: ReactNode; title: string; meta: string }[] = [
    { icon: <IDoc s={15} />, title: "Статті", meta: "19" },
    { icon: <IMic s={15} />, title: "Подкасти", meta: "20" },
    { icon: <IPlay s={14} />, title: "Уроки від спікерів", meta: "30" },
  ];
  return (
    <div className="w-full">
      <div className="overflow-hidden rounded-2xl bg-surface px-1.5 py-1 shadow-card-soft">
        {rows.map((r, i) => (
          <div
            key={r.title}
            className={"flex items-center gap-2.5 px-2.5 py-2.5 " + (i < rows.length - 1 ? "border-b border-ink/[0.06]" : "")}
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[0.625rem] bg-primary/10 text-primary">{r.icon}</span>
            <span className="flex-1 text-[0.78125rem] font-semibold text-ink">{r.title}</span>
            <span className="font-mono text-[0.625rem] text-ink-dim">{r.meta}</span>
            <span className="text-ink-faint">›</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Курс — стопка карток уроків (як стрічка); верхній урок 1:1 з реальним рядком
export function MockCourse() {
  return (
    <div className="relative mx-auto w-full max-w-[20rem] pt-2">
      <div className="absolute inset-x-4 top-0 h-10 rounded-2xl bg-surface/70 shadow-card-soft" />
      <div className="absolute inset-x-2 top-1 h-12 rounded-2xl bg-surface/85 shadow-card-soft" />
      <div className="relative rounded-2xl bg-surface p-3 shadow-card">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[0.6875rem] tabular-nums text-ink-dim">01</span>
          <span
            className="grid h-8 w-8 shrink-0 place-items-center rounded-[0.625rem]"
            style={{ background: "#F8F4EC", color: "#FE6B04" }}
          >
            <IPlay s={14} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-display text-[0.8125rem] font-semibold text-ink">Перші клієнти</div>
            <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-primary">
              <ICheck s={9} /> Переглянуто
            </div>
          </div>
          <span className="text-ink/40"><IChevron s={13} /></span>
        </div>
      </div>
    </div>
  );
}

// ---- рушій -------------------------------------------------------------

export interface Step {
  emoji: string;
  title: string;
  body: string;
  mock?: ReactNode;
  hero?: boolean;
}

export function InstructionFlow({
  steps,
  onDone,
  eyebrow,
  finishLabel,
  firstLabel = "Далі →",
}: {
  steps: Step[];
  onDone: () => void;
  eyebrow: string;
  finishLabel: string;
  firstLabel?: string;
}) {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState<"right" | "left">("right");
  const [busy, setBusy] = useState(false);

  // Лок скролу фону поки відкрито онбординг-оверлей (юзер 2026-06-27: «задній
  // фон під попапом все одно листається»).
  useBodyScrollLock();

  const total = steps.length;
  const isLast = step === total - 1;
  const isFirst = step === 0;
  const cur = steps[step];
  const progress = Math.round(((step + 1) / total) * 100);

  const go = (n: number) => {
    setDir(n > step ? "right" : "left");
    tg()?.HapticFeedback?.selectionChanged?.();
    setStep(n);
  };
  const finish = () => {
    setBusy(true);
    tg()?.HapticFeedback?.notificationOccurred?.("success");
    onDone();
  };
  const navBtn = "rounded-full px-5 py-2.5 font-display text-[0.8125rem] font-bold transition active:scale-[0.97]";

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-ink/70 p-3 backdrop-blur-sm">
      <div className="flex max-h-[94vh] w-full max-w-sm flex-col overflow-hidden rounded-3xl bg-bg shadow-2xl">
        <div className="px-5 pt-4">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-dim">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />{eyebrow}
            </span>
            <span className="text-[0.6875rem] font-medium text-ink-dim">{step + 1} / {total}</span>
          </div>
          <div className="mt-2.5 h-1 w-full overflow-hidden rounded-full bg-ink/10">
            <div className="h-full rounded-full bg-primary transition-all duration-300 ease-out" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div key={step} className={dir === "right" ? "instr-step-right" : "instr-step-left"}>
            {/* Брендова hero-панель з першого кроку — НА ВСІХ кроках (юзеру
                сподобався фон): оранжевий напівпрозорий прямокутник + декор-
                крапки. Прибрано ЛИШЕ сяйво-ореол під емодзі. На вітальному —
                ще й лого зверху. */}
            <div className="flex flex-col items-center text-center">
              <div className="relative w-full overflow-hidden rounded-[1.375rem] bg-gradient-to-b from-primary/15 via-primary/[0.06] to-transparent px-5 pb-2 pt-5">
                <span className="absolute left-5 top-4 h-2 w-2 rounded-full bg-primary/30" />
                <span className="absolute right-7 top-7 h-1.5 w-1.5 rounded-full bg-primary/40" />
                <span className="absolute bottom-3 left-9 h-1.5 w-1.5 rounded-full bg-primary/25" />
                <span className="absolute bottom-4 right-6 h-2 w-2 rounded-full bg-primary/20" />
                {cur.hero && (
                  <div className="mb-3 flex justify-center">
                    <InflowLogo size={18} />
                  </div>
                )}
                <div className="flex items-center justify-center">
                  <TgEmoji char={cur.emoji} size={58} />
                </div>
              </div>
              <h2 className="mt-2 font-display text-[1.1875rem] font-extrabold leading-tight text-ink">{cur.title}</h2>
              <p className="mx-auto mt-2 max-w-[18.75rem] text-balance text-[0.84375rem] leading-relaxed text-ink-dim">{cur.body}</p>
              {cur.mock && <div className="mt-4 w-full text-left">{cur.mock}</div>}
            </div>
          </div>
        </div>

        <div className="border-t border-ink/5 px-5 py-3.5">
          {/* key на кожній гілці — щоб React НЕ переюзав «Почати»-кнопку під
              «Назад» (інакше transition фону давав оранжевий блик). */}
          {isLast ? (
            // Останній крок: кнопка на всю ширину, БЕЗ «Назад» (текст в один рядок)
            <button
              key="finish"
              type="button"
              onClick={finish}
              disabled={busy}
              className={navBtn + " w-full bg-primary text-white active:bg-primary-deep disabled:opacity-60"}
            >
              {busy ? "…" : finishLabel}
            </button>
          ) : isFirst ? (
            <div key="first" className="flex justify-center">
              <button type="button" onClick={() => go(1)} className={navBtn + " bg-primary px-8 text-white active:bg-primary-deep"}>{firstLabel}</button>
            </div>
          ) : (
            <div key="middle" className="flex items-center justify-between">
              <button type="button" onClick={() => go(step - 1)} className="py-2.5 font-display text-[0.8125rem] font-bold text-ink-dim transition active:opacity-60">← Назад</button>
              <button type="button" onClick={() => go(step + 1)} className={navBtn + " bg-primary text-white active:bg-primary-deep"}>Далі →</button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
